import { h } from "preact";

import AvatarPreviewPopover from "../../apps/containers/AvatarPreviewPopover";
import UserAvatar from "../../apps/containers/UserAvatar";
import User from "../../foundation/api/model/users/User";
import classname from "../../foundation/utils/classname";
import permissions from "../../foundation/utils/permissions";
import { getUserDisplayName } from "../../foundation/utils/user";
import { PopoverPosition } from "../v2/Popover";

import "./style.css";

const DEFAULT_AVATAR_SIZE = 30;
const DEFAULT_MEMBER_LIMIT = 7;

// #region Types

type MemberModel = { user: User; };

// #endregion

// #region Utils

function getTitle(user: User, role: string | undefined) {
    const showEmail = role && permissions.canSeeOthersEmail(role);
    if (user.status !== "invited") {
        return getUserDisplayName(user) + (showEmail ? ` (${user.email})` : "");
    }

    return `Pending… (${user.email})`;
}

// #endregion

// #region RemainingMembers
interface RemainingMembersProps {
    remainingMembers: MemberModel[];
    currentMemberRole?: string;
    class?: string;
}

function RemainingMembers({
    remainingMembers,
    currentMemberRole,
    class: cssClass
}: RemainingMembersProps) {
    const text = `+${remainingMembers.length}`;
    const title = remainingMembers.map(userObj => getTitle(userObj.user, currentMemberRole)).join("\n");
    return (
        <div class={classname("remainingMembers", cssClass)} title={title}>
            {text}
        </div>
    );
}

// #endregion

// #region Member

interface MemberProps {
    member: MemberModel;
    avatarSize: number;
    currentMemberRole?: string;
    showCardOnHover?: boolean;
}

function Member({
    member,
    avatarSize,
    currentMemberRole,
    showCardOnHover
}: MemberProps) {
    const { user } = member;
    const title = getTitle(user, currentMemberRole);
    const avatar = (
        <UserAvatar
            class={showCardOnHover ? undefined : "avatar"}
            user={user}
            avatarProps={{ title: showCardOnHover ? undefined : title }}
            size={avatarSize}/>
    );

    return showCardOnHover ? (
        <AvatarPreviewPopover
            class="avatar"
            position={PopoverPosition.Right}
            member={member}>
            {avatar}
        </AvatarPreviewPopover>
    ) : avatar;
}

// #endregion

// #region Members

interface MembersProps {
    members: MemberModel[];
    memberLimit?: number;
    currentMemberRole?: string;
    class?: string;
    remainingMembersClass?: string;
    avatarSize?: number;
    stacked?: boolean;
    showCardOnHover?: boolean;
}

function Members({
    members,
    memberLimit = DEFAULT_MEMBER_LIMIT,
    currentMemberRole,
    class: cssClass,
    remainingMembersClass,
    avatarSize = DEFAULT_AVATAR_SIZE,
    stacked,
    showCardOnHover = false
}: MembersProps) {
    const membersBelowLimit = members.slice(0, memberLimit - 1);
    const membersLength = members.length;
    let lastChild;
    if (membersLength > memberLimit) {
        lastChild = (
            <RemainingMembers
                class={remainingMembersClass}
                key="remainingMembers"
                currentMemberRole={currentMemberRole}
                remainingMembers={members.slice(memberLimit - 1)}/>
        );
    } else if (membersLength === memberLimit) {
        lastChild = (
            <Member
                key="lastUser"
                member={members[membersLength - 1]}
                avatarSize={avatarSize}
                currentMemberRole={currentMemberRole}
                showCardOnHover={showCardOnHover}/>
        );
    }

    return (
        <div class={classname("memberAvatarList", cssClass, { stacked })}>
            {membersBelowLimit.map(member => (
                <Member
                    key={member.user._id}
                    member={member}
                    avatarSize={avatarSize}
                    currentMemberRole={currentMemberRole}
                    showCardOnHover={showCardOnHover}/>
            ))}
            {lastChild}
        </div>
    );
}

// #endregion

export default Members;
